<script lang="ts">
    import { daysOfWeekStrings, type CalendarSettings } from "@/Common/constants";
    import * as Popover from "$lib/components/ui/popover";
    import AvailabilityPopover from "@/SetupWizards/AvailabilityPopover.svelte";
    import { isSystemChosen, usualIntensity } from "@/SetupWizards/availability";
    import { MedianAndWeatherStores } from "@/MyFitness/stores";
    import { format } from "date-fns";
    import { getContext } from "svelte";
    import { getDayMedians, getDisplayMedians, getUsualAvailability, uniqueId } from "@/Util/utils";
    import { Accordion } from "@/Common";
    import { cn } from "@/lib/utils";

    export let alwaysOpen = false
    export let cal_settings: CalendarSettings
    let className = "";
    export { className as class };
    export let onSubmit = () => {}


    let { trainingMedians } = getContext<MedianAndWeatherStores>('medianAndWeather')

    const id = uniqueId("trainPrefDayPicker-")
    // The popover writes its edits to the week's settings as they are made; the forecast inputs are asked again when it closes
    // after a change.
    let before = ""
    const onOpenChange = (open: boolean) => {
        if (open) before = JSON.stringify(cal_settings.defaults)
        else if (JSON.stringify(cal_settings.defaults) !== before) onSubmit()
    }

</script>

<div class={cn(className)}>
    <Accordion {alwaysOpen} class="py-2 px-4 border border-solid border-black/10 rounded">
        <div slot="trigger" class="text-left {alwaysOpen ? '' : 'text-slate-600'}">Weekly Availability</div>
        <div slot="content">
            {#if !alwaysOpen} <div class="text-slate-400 italic text-sm text-left mb-2">*Changes will not apply globally until the program is saved.</div> {/if}
            <div  class="this-grid text-sm px-1 py-2">

                {#if cal_settings && $trainingMedians}
                
                    {#each daysOfWeekStrings as [dow, dowLabel, dow3Letter]}
                        {@const availability = cal_settings.defaults[dow3Letter]}
                        {@const displayMedians = getDisplayMedians(getDayMedians($trainingMedians, dow))}
                        <span class="justify-self-start mr-[6px]">{dowLabel}</span>
                        <span class="text-left">
                            {#if !availability.availability}
                                <span class="text-red-600">
                                    <i class="fa-solid fa-ban" />
                                    Unavailable
                                </span>
                            {:else}
                                {@const duration = availability.duration > 0 ? availability.duration * 60 : displayMedians.duration * 3600}
                                {@const hours = Math.floor(duration / 3600)}
                                {@const minutes = Math.floor((duration / 60) % 60)}

                                <span class={availability.duration > 0 || availability.time != 'advisor' ? 'text-yellow-600': 'text-slate-500'}>
                                    {#if !availability.fixed_availability}
                                        {'~ '}
                                    {/if}
                                    {#if hours > 0}
                                        {hours}h
                                    {/if}
                                    {#if minutes > 0}
                                        {minutes}m
                                    {/if}
                                    @
                                    {#if availability.time != 'advisor'}
                                        {availability.time.slice(0, 5)}
                                    {:else}
                                        {format(displayMedians.timedDate, 'h:mm aaa')}
                                    {/if}
                                </span>
                                {#if availability.intensity === 'low' || availability.intensity === 'high'}
                                    <span class="ml-1 text-xerteal-600 capitalize">· {availability.intensity}</span>
                                {/if}
                            {/if}
                        </span>
                        <Popover.Root portal="#shadcn-ui-dialog-target" {onOpenChange}>
                            <Popover.Trigger class="btn-xfai-sm outline-none text-xerteal-500 border-solid border-transparent hover:border-xerteal-600 border-1 bg-transparent relative" type="button">
                                <i class="fa-solid fa-pen-to-square" />
                                <div class:hidden={availability.availability && isSystemChosen(availability, usualIntensity(getUsualAvailability($trainingMedians, dow)))} class="yellow-dot" />
                            </Popover.Trigger>
                            <AvailabilityPopover bind:defaultAvailability={cal_settings.defaults[dow3Letter]} {dowLabel} id="{id}-{dow3Letter}" medians={getUsualAvailability($trainingMedians, dow)} />
                        </Popover.Root>
                    {/each}
                {:else}
                    <div class="text-slate-600 text-center">Loading...</div>
                {/if}

            </div>
        </div>
    </Accordion>
</div>

<style>
    .yellow-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        position: absolute;
        top: 3px;
        right: calc(50% - 10px);
        background-color: rgb(255, 167, 0);
    }

    .this-grid {
        display: grid;
        grid-template-columns: max-content auto max-content;
        grid-auto-flow: row;
        gap: 0.25rem;
        align-items: center;
    }
</style>